<script>
import { Bar } from "vue-chartjs";
import moment from "moment";
import Chart from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
import { OBSERVATORY } from "../../../enums/Type";
Chart.plugins.register(ChartAnnotationsPlugin);

export default {
  extends: Bar,
  name: "ManholeChart",

  props: {
    observatory: {
      type: Object
    },
    timeSeries: {
      type: Array
    },
    currentLevel: {
      type: Number
    }
  },

  data() {
    return {
      dataCollection: {
        labels: [],
        datasets: [
          {
            type: "line",
            label: "水位",
            unit: "m",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "level",
            borderWidth: 2,
            borderColor: "#01163F",
            backgroundColor: "#01163F",
            pointBorderColor: "#01163F",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: false
          },
          {
            type: "line",
            label: "水位",
            unit: "m",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "level",
            borderWidth: 3,
            borderColor: "#3734dc",
            backgroundColor: "#3734dc",
            pointBorderColor: "#3734dc",
            borderDash: [10, 5],
            pointRadius: 0,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: true
          }
        ]
      },
      options: {
        layout: {
          padding: {
            right: 0
          }
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const unit = data.datasets[tooltipItem.datasetIndex].unit;
              const value =
                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return label + " : " + parseFloat(value).toFixed(2) + " " + unit;
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "level",
              gridLines: {
                display: false
              },
              ticks: {
                max: 20,
                min: 0,
                stepSize: 0.5,
                callback: label => {
                  return parseFloat(label).toFixed(1);
                }
              }
            }
          ],
          xAxes: [
            {
              id: "data",
              type: "time",
              gridLines: {
                display: false
              },
              ticks: {
                display: true,
                autoSkip: true
              },
              time: {
                unit: "hour",
                unitStepSize: 1,
                tooltipFormat: "MM/DD HH:mm",
                displayFormats: {
                  minute: "HH:mm",
                  hour: "HH:mm",
                  day: "HH:mm",
                  week: "HH:mm",
                  month: "HH:mm"
                }
              },
              categoryPercentage: 1.0,
              barPercentage: 1.0
            }
          ]
        },
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "index",
          intersect: false
        },
        groundDataset: [],
        annotation: {
          events: ["click", "dblclick", "mouseover", "mouseout"],
          annotations: [
            {
              type: "box",
              drawTime: "beforeDatasetsDraw",
              yScaleID: "level",
              borderColor: "rgba(0,0,0,0)",
              yMin: 0,
              yMax: 0,
              backgroundColor: "#3D8AFF"
            },
            {
              type: "line",
              mode: "vertical",
              scaleID: "data",
              value: "02:00",
              borderColor: "#FF0000",
              borderWidth: 1.5,
              label: {
                content: "現在",
                position: "top",
                enabled: true
              }
            }
          ]
        }
      }
    };
  },

  computed: {
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    minDate() {
      return this.baseDate.clone().subtract(200, "minutes");
    },
    maxDate() {
      return this.baseDate.clone().add(70, "minutes");
    },
    landformLabels() {
      if (!this.observatory.labels) {
        return [];
      } else {
        return this.observatory.labels;
      }
    }
  },

  watch: {
    timeSeries() {
      this.setTimeSeries();
      setTimeout(() => {
        this.renderChart(this.dataCollection, this.options);
      }, 100);
    }
  },

  mounted() {
    this.setTimeSeries();

    const img = this.$store.getters.manholeChartTopImage;
    this.addPlugin({
      id: "horizontalLine",
      beforeDatasetsDraw: chart => {
        const groundDataset = chart.config.options.groundDataset;
        if (groundDataset.length > 0) {
          const ctx = chart.chart.ctx;
          const xAxe = chart.scales["data"];
          const yAxe = chart.scales["level"];

          const arrX = groundDataset.map(o => {
            return o[0];
          });

          const arrY = groundDataset.map(o => {
            return o[1];
          });

          const minGround = groundDataset.find(o => o[0] === Math.min(...arrX));
          const maxGround = groundDataset.find(o => o[0] === Math.max(...arrX));

          const maxGroundY = Math.max(...arrY);
          const minGroundY = Math.min(...arrY);

          const getX = baseX => {
            return (
              ((baseX - minGround[0]) / (maxGround[0] - minGround[0])) *
                xAxe.width +
              xAxe.left
            );
          };

          const getY = baseY => {
            return (
              ((yAxe.max - yAxe.min - (baseY - yAxe.min)) /
                (yAxe.max - yAxe.min)) *
                yAxe.height +
              yAxe.top
            );
          };

          ctx.beginPath();
          ctx.moveTo(xAxe.left, yAxe.bottom);
          for (let i = 0; i < groundDataset.length; i++) {
            ctx.lineTo(getX(groundDataset[i][0]), getY(groundDataset[i][1]));
          }
          ctx.lineTo(xAxe.right, yAxe.bottom);
          ctx.lineTo(xAxe.left, yAxe.bottom);
          ctx.closePath();
          ctx.fillStyle = "#cf9e80";
          ctx.fill();

          let manholeHeight = (maxGroundY - minGroundY) / 15;
          if (manholeHeight > 0.5) {
            manholeHeight = 0.5;
          }

          ctx.beginPath();
          ctx.moveTo(xAxe.left, getY(maxGroundY));
          ctx.lineTo(xAxe.left, getY(maxGroundY - manholeHeight));
          ctx.lineTo(xAxe.right, getY(maxGroundY - manholeHeight));
          ctx.lineTo(xAxe.right, getY(maxGroundY));
          ctx.closePath();
          ctx.fillStyle = "#f1eeea";
          ctx.fill();

          if (this.observatory.type === OBSERVATORY.MANHOLE.code) {
            ctx.beginPath();
            ctx.moveTo(getX(45), getY(maxGroundY));
            ctx.lineTo(getX(45), getY(maxGroundY - manholeHeight));
            ctx.lineTo(getX(55), getY(maxGroundY - manholeHeight));
            ctx.lineTo(getX(55), getY(maxGroundY));
            ctx.closePath();
            ctx.fillStyle = "#a8a8a8";
            ctx.fill();
            ctx.font = "8px Arial";
            ctx.fillStyle = "#333333";
            ctx.fillText("マンホール", getX(44), getY(maxGroundY) - 5);
          }

          if (
            this.observatory.type === OBSERVATORY.MANHOLE.code &&
            this.observatory.id === "K43"
          ) {
            const y = getY(1.7);
            ctx.beginPath();
            ctx.moveTo(getX(4), y - 11);
            ctx.lineTo(getX(8), y - 11);
            ctx.lineTo(getX(8), y - 7);
            ctx.lineTo(getX(4), y - 7);
            ctx.lineTo(getX(4), y - 3);
            ctx.lineTo(getX(1), y - 9);
            ctx.lineTo(getX(4), y - 15);
            ctx.closePath();
            ctx.fillStyle = "#666666";
            ctx.fill();
            ctx.font = "11px Arial";
            ctx.fillStyle = "#000000";
            ctx.fillText("流下方向", getX(10), y - 5);
          }

          if (img !== null) {
            let w = getX(45) - getX(30);
            let h = w * (img.height / img.width);
            if (h > getY(maxGroundY) - yAxe.top) {
              h = getY(maxGroundY) - yAxe.top;
              w = h * (img.width / img.height);
            }
            ctx.drawImage(img, getX(42) - w, getY(maxGroundY) - h, w, h);
          }
        }
      }
    });

    this.renderChart(this.dataCollection, this.options);
  },

  methods: {
    setTimeSeries() {
      // labels
      this.dataCollection.labels = this.timeSeries.map(ts => {
        return moment
          .utc(ts.date, "YYYYMMDDHHmm")
          .local()
          .toDate();
      });

      // x scales (date range)
      this.options.scales.xAxes[0].ticks.min = this.minDate.toDate();
      this.options.scales.xAxes[0].ticks.max = this.maxDate.toDate();

      // levels
      const levels = this.timeSeries.map(r => {
        return r.level;
      });
      const realLevels = this.timeSeries.map(r =>
        r.type === "real" ? r.level : null
      );
      const forecastLevels = this.timeSeries.map(r =>
        r.type === "forecast" ? r.level : null
      );
      this.dataCollection.datasets[0].data = Object.assign(realLevels);
      this.dataCollection.datasets[1].data = Object.assign(forecastLevels);

      // water box
      this.options.annotation.annotations[0].yMin = this.options.scales.yAxes[0].ticks.min;
      if (this.currentLevel !== null && this.currentLevel !== undefined) {
        this.options.annotation.annotations[0].yMax = this.currentLevel;
      } else {
        if (this.timeSeries.length === 0) {
          this.options.annotation.annotations[0].yMax = 0;
        } else {
          const latestData = this.timeSeries[this.timeSeries.length - 1];
          this.options.annotation.annotations[0].yMax = latestData.level;
        }
      }

      // base date
      this.options.annotation.annotations[1].value = this.baseDate
        .local()
        .toDate();

      // landform
      this.options.groundDataset = this.observatory.landform
        ? this.observatory.landform
        : [];

      // y scales
      let minLevel = Math.min(...levels);
      let maxLevel = Math.max(...levels);
      if (this.options.groundDataset.length > 0) {
        const yArray = this.options.groundDataset.map(o => {
          return o[1];
        });
        minLevel = Math.min(minLevel, ...yArray);
        maxLevel = Math.ceil(Math.max(maxLevel, ...yArray)) + 2;
      } else {
        minLevel = Math.floor(minLevel);
        maxLevel = Math.ceil(maxLevel + 0.5);
      }
      this.options.scales.yAxes[0].ticks.min = minLevel;
      this.options.scales.yAxes[0].ticks.max = maxLevel;

      // landformLabels
      this.options.annotation.annotations.splice(2);
      this.landformLabels.forEach(label => {
        this.options.annotation.annotations.push({
          type: "line",
          mode: "horizontal",
          scaleID: "level",
          value: label.value,
          borderColor: "transparent",
          borderWidth: 0,
          label: {
            enabled: true,
            content: label.name,
            position: "left",
            yAdjust: -10,
            fontSize: 12,
            fontColor: "black",
            fontStyle: "normal",
            backgroundColor: "transparent"
          }
        });
      });
    }
  }
};
</script>

<style scoped></style>
